import { classes } from "polytype";
import {
  AggregateMetaData,
  Bytes,
  DataModel,
  ListDataModel,
  ListQueryModel,
  ReferenceModel,
} from "../../base";
import assertObjectFound from "../../base/assertObjectFound.js";
import { config } from "../../config/config.js";
import { Project } from "../../project/";
import type { AsyncResourceVariant } from "../../react";
import { provideReact } from "../../react";
import { Autoresponder } from "../Autoresponder";
import type {
  AutoresponderUpdateRequestData,
  ForwardRequestData,
  MailAddressData,
  MailAddressListItemData,
  MailAddressListQueryData,
  MailAddressRequestData,
} from "./types.js";

export class MailAddress extends ReferenceModel {
  public static aggregateMetaData = new AggregateMetaData(
    "mail",
    "mailaddress",
  );

  public static readonly mailboxMaxQuotaInGb = 100;
  public static readonly mailboxMinQuotaInGb = 0.2;
  public static readonly mailboxQuotaStepInGb = 0.1;
  public static readonly quotaUsageWarning = 0.75;
  public static readonly quotaUsageError = 0.9;
  public static readonly mailServer = "mail.agenturserver.de";
  public static readonly imapStartTls = "143";
  public static readonly imapSsl = "993";
  public static readonly pop3StartTls = "110";
  public static readonly pop3Ssl = "995";
  public static readonly smtpStartTls = "25";
  public static readonly smtpSsl = "465";

  public static ofId(id: string) {
    return new MailAddress(id);
  }

  public static find = provideReact(async (id: string) => {
    const data = await config.behaviors.mailAddress.find(id);
    if (data !== undefined) {
      return new MailAddressDetailed(data);
    }
  });

  public static get = provideReact(async (id: string) => {
    const mailAddress = await this.find(id);
    assertObjectFound(mailAddress, "mailAddress", id);
    return mailAddress;
  });

  public findDetailed = provideReact(
    () => MailAddress.find(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<MailAddressDetailed | undefined>>;

  public getDetailed = provideReact(
    () => MailAddress.get(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<MailAddressDetailed>>;

  public static query(project: Project, query: MailAddressListQueryData = {}) {
    return new MailAddressListQuery(project, query);
  }

  public static async createMailAddress(
    project: Project,
    data: MailAddressRequestData,
  ) {
    const response = await config.behaviors.mailAddress.createMailAddress(
      project.id,
      data,
    );

    if ("id" in response) {
      return new MailAddress(response.id);
    }

    return response;
  }

  public static async createForward(
    project: Project,
    data: ForwardRequestData,
  ) {
    const response = await config.behaviors.mailAddress.createForward(
      project.id,
      data,
    );

    if ("id" in response) {
      return new MailAddress(response.id);
    }

    return response.map((e) => ({
      ...e,
      path: e.path.includes("forward_address") ? "forwardAddress" : e.path,
      type: e.message?.includes("must not forward to itself")
        ? "forwardToItself"
        : e.type,
    }));
  }
  public async updateAddress(address: string) {
    return await config.behaviors.mailAddress.updateAddress(this.id, address);
  }
  public async updatePassword(password: string) {
    await config.behaviors.mailAddress.updatePassword(this.id, password);
  }

  public async updateQuota(quotaInGb: number) {
    await config.behaviors.mailAddress.updateQuota(
      this.id,
      Bytes.of(quotaInGb, "GiB").in("bytes"),
    );
  }

  public async updateForwardAddresses(addresses: string[]) {
    const response = await config.behaviors.mailAddress.updateForwardAddresses(
      this.id,
      addresses,
    );

    if (response) {
      return response.map((e) => ({
        ...e,
        path: e.path.includes("address") ? "forwardAddress" : e.path,
        type: e.message?.includes("must not forward to itself")
          ? "forwardToItself"
          : e.type,
      }));
    }
  }

  public async deactivateCatchAll() {
    await config.behaviors.mailAddress.updateCatchAll(this.id, false);
  }

  public async activateCatchAll() {
    await config.behaviors.mailAddress.updateCatchAll(this.id, true);
  }

  public async updateAutoResponder(data: AutoresponderUpdateRequestData) {
    await config.behaviors.mailAddress.updateAutoResponder(this.id, data);
  }

  public async delete() {
    await config.behaviors.mailAddress.delete(this.id);
  }
}

export class MailAddressCommon extends classes(
  DataModel<MailAddressData>,
  MailAddress,
) {
  public readonly project: Project;
  public readonly isForward: boolean;
  public readonly receivingDisabled: boolean;
  public readonly sendingDisabled: boolean;
  public readonly storageUsagePercent: number;
  public readonly storageAlmostExceeded: boolean;
  public readonly storageExceeded: boolean;
  public readonly storageLimit: Bytes;
  public readonly storageUsage: Bytes;
  public readonly autoresponder: Autoresponder;
  public readonly address: string;
  public readonly isArchived: boolean;
  public readonly isCatchAll: boolean;
  public readonly forwardAddresses: string[];
  public readonly domain?: string;
  public readonly localPart?: string;
  public readonly spamProtectionActive: boolean;

  public constructor(data: MailAddressData) {
    super([data], [data.id]);
    this.project = Project.ofId(data.projectId);
    this.isForward = !this.data.mailbox;
    this.receivingDisabled = data.receivingDisabled;
    this.sendingDisabled = data.mailbox?.sendingEnabled === false;
    this.storageUsagePercent = this.data.mailbox
      ? this.data.mailbox.storageInBytes.current.value /
        this.data.mailbox.storageInBytes.limit
      : 0;
    this.storageExceeded =
      this.storageUsagePercent >= MailAddress.quotaUsageError;
    this.storageAlmostExceeded =
      !this.storageExceeded &&
      this.storageUsagePercent >= MailAddress.quotaUsageWarning;
    this.storageLimit = Bytes.of(
      data.mailbox?.storageInBytes.limit ?? 0,
      "bytes",
    );
    this.storageUsage = Bytes.of(
      data.mailbox?.storageInBytes.current.value ?? 0,
      "bytes",
    );
    this.autoresponder = new Autoresponder(data.autoResponder);
    this.address = data.address;
    this.isArchived = data.isArchived;
    this.isCatchAll = data.isCatchAll;
    this.forwardAddresses = data.forwardAddresses;
    this.domain = data.address.split("@").at(1);
    this.localPart = data.address.split("@").at(0);
    this.spamProtectionActive = data.mailbox?.spamProtection.active ?? false;
  }

  public async addForwardAddress(address: string) {
    return await this.updateForwardAddresses([
      ...this.data.forwardAddresses,
      address,
    ]);
  }

  public async removeForwardAddress(address: string) {
    await this.updateForwardAddresses(
      this.data.forwardAddresses.filter((a) => a !== address),
    );
  }

  public async updateSpamProtection(active: boolean) {
    await config.behaviors.mailAddress.updateSpamProtection(this.id, {
      active,
      autoDeleteSpam: this.data.mailbox?.spamProtection.autoDeleteSpam ?? false,
      folder: this.data.mailbox?.spamProtection.folder ?? "spam",
      relocationMinSpamScore:
        this.data.mailbox?.spamProtection.relocationMinSpamScore ?? 1,
    });
  }

  public async deactivateAutoResponder() {
    await this.updateAutoResponder({
      active: false,
      message: this.autoresponder.message ?? "",
    });
  }
}

export class MailAddressDetailed extends classes(
  MailAddressCommon,
  DataModel<MailAddressData>,
) {
  public constructor(data: MailAddressData) {
    super([data], [data]);
  }
}

export class MailAddressListItem extends classes(
  MailAddressCommon,
  DataModel<MailAddressData>,
) {
  public constructor(data: MailAddressListItemData) {
    super([data], [data]);
  }
}

export class MailAddressListQuery extends ListQueryModel<MailAddressListQueryData> {
  public readonly project: Project;
  public constructor(project: Project, query: MailAddressListQueryData = {}) {
    super(query, {
      dependencies: [project.id],
    });
    this.project = project;
  }

  public refine(query: MailAddressListQueryData = {}) {
    return new MailAddressListQuery(this.project, {
      ...this.query,
      ...query,
    });
  }

  public execute = provideReact(async () => {
    const { items, totalCount } = await config.behaviors.mailAddress.list(
      this.project.id,
      {
        limit: config.defaultPaginationLimit,
        ...this.query,
      },
    );
    return new MailAddressList(
      this.project,
      this.query,
      items.map((m) => new MailAddressListItem(m)),
      totalCount,
    );
  }, [this.queryId, "mailAddress"]);

  public getTotalCount = provideReact(async () => {
    const { items } = await this.refine({ limit: 1 }).execute();
    return items.length;
  }, [this.queryId, "mailAddress"]);
}

export class MailAddressList extends classes(
  MailAddressListQuery,
  ListDataModel<MailAddressListItem>,
) {
  public constructor(
    project: Project,
    query: MailAddressListQueryData,
    mailAddresses: MailAddressListItem[],
    totalCount: number,
  ) {
    super([project, query], [mailAddresses, totalCount]);
  }
}
